/**
 * Update Community Use Case
 * Business logic for updating an existing community
 */

import type { CommunitiesRepository } from '../data/communities-repository';
import type { UpdateCommunityRequest, UpdateCommunityResponse } from '../types/communities-types';

export class UpdateCommunityUseCase {
  constructor(private readonly repository: CommunitiesRepository) {}

  /**
   * Execute use case to update a community
   * @param id - Community profile ID
   * @param data - Updated community data (partial)
   */
  async execute(id: number, data: UpdateCommunityRequest): Promise<UpdateCommunityResponse> {
    try {
      // Validate profile ID
      if (!id || id <= 0) {
        throw new Error('Valid community ID is required');
      }

      // Validate dates if provided
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

      if (data.startDate && !dateRegex.test(data.startDate)) {
        throw new Error('Start date must be in YYYY-MM-DD format');
      }

      if (data.expirationDate && !dateRegex.test(data.expirationDate)) {
        throw new Error('Expiration date must be in YYYY-MM-DD format');
      }

      // Validate expiration date is after start date (if both provided)
      if (data.startDate && data.expirationDate) {
        const start = new Date(data.startDate);
        const expiry = new Date(data.expirationDate);

        if (expiry <= start) {
          throw new Error('Expiration date must be after start date');
        }
      }

      // Validate strategy type if provided
      if (data.strategyType !== undefined && ![0, 1, 2].includes(data.strategyType)) {
        throw new Error('Valid strategy type is required (0=EnergyPercentage, 1=ByKwh, 2=TotalPercentage)');
      }

      // Validate profile status if provided
      if (data.profileStatus !== undefined && ![0, 1].includes(data.profileStatus)) {
        throw new Error('Valid profile status is required (1=Deleted, 0=Active)');
      }

      const response = await this.repository.updateCommunity(id, data);

      if (!response.success) {
        throw new Error(response.message || 'Failed to update community');
      }

      return response;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update community';
      throw new Error(message);
    }
  }
}
